import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

function CopyReview({ apiUrl, getStatusBadge, formatStatus }) {
  const { pieceId } = useParams()
  const navigate = useNavigate()
  const [piece, setPiece] = useState(null)
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDraft, setSelectedDraft] = useState(null)
  const [editedCopy, setEditedCopy] = useState('')
  const [editedHashtags, setEditedHashtags] = useState('')
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [activeTab, setActiveTab] = useState('view') // 'view' | 'edit'
  const [comparisonMode, setComparisonMode] = useState(false)

  useEffect(() => {
    fetchPiece()
  }, [pieceId])

  const fetchPiece = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}`)
      const data = await res.json()
      setPiece(data.piece)
      setDrafts(data.drafts || [])
      
      // Auto-select first draft if none selected
      if (data.drafts?.length > 0 && !selectedDraft) {
        setSelectedDraft(data.drafts[0].id)
        setEditedCopy(data.drafts[0].copy_text || '')
        setEditedHashtags(data.drafts[0].hashtags || '')
      }
    } catch (e) {
      console.error('Failed to fetch:', e)
    } finally {
      setLoading(false)
    }
  }

  const selectedDraftData = drafts.find(d => d.id === selectedDraft)

  const handleDraftSelect = (draftId) => {
    setSelectedDraft(draftId)
    const draft = drafts.find(d => d.id === draftId)
    if (draft) {
      setEditedCopy(draft.copy_text || '')
      setEditedHashtags(draft.hashtags || '')
    }
    setActiveTab('view')
  }

  const handleSaveDraft = async () => {
    if (!selectedDraft) return
    
    setSaving(true)
    setSaveMessage('')
    
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}/drafts/${selectedDraft}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          copy_text: editedCopy,
          hashtags: editedHashtags
        })
      })
      
      if (res.ok) {
        // Update local state
        setDrafts(prev => prev.map(d => 
          d.id === selectedDraft 
            ? { ...d, copy_text: editedCopy, hashtags: editedHashtags }
            : d
        ))
        setSaveMessage('✓ Saved successfully')
        setTimeout(() => setSaveMessage(''), 3000)
      } else {
        setSaveMessage('❌ Failed to save')
      }
    } catch (e) {
      console.error('Failed to save:', e)
      setSaveMessage('❌ Error saving')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!selectedDraft) {
      alert('Please select a draft to approve')
      return
    }
    
    setSubmitting(true)
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}/approve-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          draft_id: selectedDraft,
          final_copy: editedCopy !== selectedDraftData?.copy_text ? editedCopy : undefined
        })
      })
      
      if (res.ok) {
        alert('✓ Draft approved! Design generation will start automatically.')
        navigate('/')
      } else {
        const err = await res.json()
        alert('Failed to approve: ' + (err.error || 'Unknown error'))
      }
    } catch (e) {
      console.error('Failed to approve:', e)
      alert('Error approving draft')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRevise = async () => {
    if (!feedback.trim()) {
      alert('Please provide feedback for revision')
      return
    }
    
    setSubmitting(true)
    try {
      const res = await fetch(`${apiUrl}/api/content/${pieceId}/request-revision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback })
      })
      
      if (res.ok) {
        alert('✓ Revision requested! New drafts will be generated.')
        navigate('/')
      } else {
        const err = await res.json()
        alert('Failed to request revision: ' + (err.error || 'Unknown error'))
      }
    } catch (e) {
      console.error('Failed to request revision:', e)
      alert('Error requesting revision')
    } finally {
      setSubmitting(false)
    }
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'bg-green-500'
    if (score >= 6) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    )
  }

  if (!piece) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-xl mb-2">Content piece not found</div>
        <button onClick={() => navigate('/')} className="text-blue-600 hover:underline">
          ← Back to Queue
        </button>
      </div>
    )
  }

  const currentRound = Math.max(...drafts.map(d => d.round || 1), 1)
  const currentDrafts = drafts.filter(d => d.round === currentRound)

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate('/')} 
          className="text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
        >
          <span>←</span> Back to Queue
        </button>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{piece.content_bucket}</h1>
              <p className="text-gray-500 mt-1">{piece.industry} · Round {currentRound}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(piece.status)}`}>
              {formatStatus(piece.status)}
            </span>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Publish Date:</span>
                <p className="font-medium">{piece.date || 'TBD'}</p>
              </div>
              <div>
                <span className="text-gray-500">Review Deadline:</span>
                <p className="font-medium text-red-600">{piece.draft_deadline}</p>
              </div>
              <div>
                <span className="text-gray-500">Drafts:</span>
                <p className="font-medium">{currentDrafts.length} variants</p>
              </div>
            </div>
          </div>
          
          {piece.content_brief && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600"><strong>Brief:</strong> {piece.content_brief}</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Draft Selector */}
        <div className="col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Select Draft</h3>
            <div className="space-y-2">
              {currentDrafts.map((draft, idx) => (
                <button
                  key={draft.id}
                  onClick={() => handleDraftSelect(draft.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    selectedDraft === draft.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Draft {String.fromCharCode(65 + idx)}</span>
                    {draft.human_score && (
                      <span className={`text-xs text-white px-2 py-1 rounded-full ${getScoreColor(draft.human_score)}`}>
                        {draft.human_score}/10
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {(draft.copy_text?.split(/\s+/)?.length || 0)} words
                  </p>
                </button>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setComparisonMode(!comparisonMode)}
                className="w-full py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                {comparisonMode ? 'Exit Comparison' : 'Compare All Drafts'}
              </button>
            </div>
          </div>
          
          {/* Actions Panel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-4">Actions</h3>
            
            <button
              onClick={handleApprove}
              disabled={submitting || !selectedDraft}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-3 rounded-lg font-medium mb-3 transition-colors flex items-center justify-center gap-2"
            >
              <span>✓</span> Approve Draft
            </button>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Request Revision:</p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What changes would you like?"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm mb-2"
                rows={3}
              />
              <button
                onClick={handleRevise}
                disabled={submitting || !feedback.trim()}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white py-2 rounded-lg font-medium text-sm transition-colors"
              >
                Request Revision
              </button>
            </div>
          </div>
        </div>

        {/* Main Panel - Draft Editor/Viewer */}
        <div className="col-span-9">
          {comparisonMode ? (
            // Comparison Mode
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Side-by-Side Comparison</h3>
              <div className="grid grid-cols-2 gap-4">
                {currentDrafts.map((draft, idx) => (
                  <div key={draft.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Draft {String.fromCharCode(65 + idx)}</span>
                      {draft.human_score && (
                        <span className={`text-xs text-white px-2 py-1 rounded-full ${getScoreColor(draft.human_score)}`}>
                          {draft.human_score}/10
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap font-serif">
                      {draft.copy_text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : selectedDraftData ? (
            // Single Draft View
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('view')}
                  className={`px-6 py-3 font-medium text-sm ${
                    activeTab === 'view'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  View
                </button>
                <button
                  onClick={() => setActiveTab('edit')}
                  className={`px-6 py-3 font-medium text-sm ${
                    activeTab === 'edit'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Edit
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {activeTab === 'view' ? (
                  <div>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-wrap text-gray-800 leading-relaxed font-serif text-lg">
                        {selectedDraftData.copy_text}
                      </div>
                    </div>
                    {selectedDraftData.hashtags && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <span className="text-sm text-gray-500">Hashtags:</span>
                        <p className="text-blue-600 mt-1">{selectedDraftData.hashtags}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content
                      </label>
                      <textarea
                        value={editedCopy}
                        onChange={(e) => setEditedCopy(e.target.value)}
                        className="w-full p-4 border border-gray-300 rounded-lg font-serif text-base leading-relaxed"
                        rows={12}
                        placeholder="Edit your draft here..."
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hashtags
                      </label>
                      <input
                        type="text"
                        value={editedHashtags}
                        onChange={(e) => setEditedHashtags(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="#hashtag1 #hashtag2"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSaveDraft}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        {saving ? (
                          <>
                            <span className="animate-spin">⟳</span> Saving...
                          </>
                        ) : (
                          <>💾 Save Changes</>
                        )}
                      </button>
                      
                      {saveMessage && (
                        <span className={`text-sm ${saveMessage.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                          {saveMessage}
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-800">
                        <strong>Tip:</strong> After editing, click "Save Changes" then switch to the "View" tab and click "Approve Draft" to proceed.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-500">Select a draft from the sidebar to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CopyReview
